/**
 * AdminCRM — Twenty-inspired Relationship Management dashboard.
 *
 * Tabs:
 *   • Overview  — KPIs, top-linked contacts, role distribution.
 *   • Contacts  — searchable, filterable table → contact detail.
 *   • Pipeline  — kanban board of opportunities by stage.
 *   • Tasks     — inbox of CRM tasks across pipelines/contacts.
 *
 * Route: /admin/crm (super admin / tenant admin).
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Users, Mail, Phone, Ban, RefreshCw, Plus, ArrowRight, Briefcase,
  Workflow, ListChecks, ChevronRight, Search, Activity, Download,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';
import { useAuthStore } from '@/lib/store';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

// ---------------------------------------------------------------------------
// Small UI atoms
// ---------------------------------------------------------------------------
const KPICard = ({ icon: Icon, label, value, sub, accent = 'indigo', testId }) => {
  const palette = {
    indigo: 'from-indigo-500/15 to-indigo-500/5 text-indigo-300 border-indigo-400/30',
    emerald: 'from-emerald-500/15 to-emerald-500/5 text-emerald-300 border-emerald-400/30',
    amber: 'from-amber-500/15 to-amber-500/5 text-amber-300 border-amber-400/30',
    rose: 'from-rose-500/15 to-rose-500/5 text-rose-300 border-rose-400/30',
    sky: 'from-sky-500/15 to-sky-500/5 text-sky-300 border-sky-400/30',
  }[accent];
  return (
    <Card data-testid={testId} className="bg-slate-900/60 border-slate-800">
      <CardContent className="p-5">
        <div className={`rounded-xl bg-gradient-to-br ${palette} border p-3 inline-flex`}>
          <Icon className="h-4 w-4" />
        </div>
        <p className="text-xs uppercase tracking-wider text-slate-400 mt-3">{label}</p>
        <p className="text-3xl font-semibold text-white mt-1">{value}</p>
        {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
};

const RolePill = ({ role, count }) => (
  <div className="flex items-center justify-between bg-slate-800/40 border border-slate-700/60 rounded-md px-3 py-2 text-sm">
    <span className="text-slate-300 capitalize">{role}</span>
    <span className="text-slate-400 tabular-nums">{count}</span>
  </div>
);

// ---------------------------------------------------------------------------
// Tab 1 — Overview
// ---------------------------------------------------------------------------
const OverviewTab = ({ tenantId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['crm-dashboard', tenantId],
    queryFn: async () => {
      const params = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
      const { data } = await axios.get(`${API}/api/crm/dashboard${params}`, auth());
      return data;
    },
  });
  if (isLoading || !data) {
    return <p className="text-slate-400 text-sm">Loading dashboard…</p>;
  }
  return (
    <div className="space-y-6" data-testid="crm-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPICard icon={Users} label="Total contacts" value={data.total_contacts}
                 accent="indigo" testId="kpi-crm-total" />
        <KPICard icon={Mail} label="With email" value={data.with_email}
                 sub={`${Math.round((data.with_email / Math.max(1, data.total_contacts)) * 100)}% reachable`}
                 accent="sky" testId="kpi-crm-email" />
        <KPICard icon={Phone} label="With phone" value={data.with_phone}
                 accent="emerald" testId="kpi-crm-phone" />
        <KPICard icon={Ban} label="Do-not-contact" value={data.do_not_contact}
                 accent="rose" testId="kpi-crm-dnc" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader>
            <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
              <Briefcase className="h-4 w-4" /> Roles in your graph
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {Object.entries(data.by_role || {}).map(([role, n]) => (
              <RolePill key={role} role={role} count={n} />
            ))}
            {!Object.keys(data.by_role || {}).length && (
              <p className="text-xs text-slate-500">No tagged contacts yet.</p>
            )}
          </CardContent>
        </Card>

        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader>
            <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
              <Activity className="h-4 w-4" /> Most-connected people
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {(data.top_linked || []).map(c => (
              <Link key={c.id} to={`/admin/crm/contacts/${c.id}`}
                    className="flex items-center justify-between bg-slate-800/40 hover:bg-slate-800/70 transition border border-slate-700/60 rounded-md px-3 py-2 text-sm"
                    data-testid={`crm-top-linked-${c.id}`}>
                <div>
                  <div className="text-slate-200">{c.name || '—'}</div>
                  <div className="text-xs text-slate-500 flex gap-1 mt-0.5">
                    {(c.roles || []).map(r => (
                      <Badge key={r} variant="outline" className="text-[10px] py-0">{r}</Badge>
                    ))}
                  </div>
                </div>
                <div className="text-xs text-slate-400 tabular-nums">{c.links} links</div>
              </Link>
            ))}
            {!data.top_linked?.length && (
              <p className="text-xs text-slate-500">Run an import to build your graph.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 2 — Contacts
// ---------------------------------------------------------------------------
const ContactsTab = ({ tenantId }) => {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [newC, setNewC] = useState({ full_name: '', email: '', phone: '', role: '', organisation: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['crm-contacts', tenantId, q, role, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (tenantId) params.set('tenant_id', tenantId);
      if (q) params.set('q', q);
      if (role) params.set('role', role);
      params.set('page', page);
      const { data } = await axios.get(`${API}/api/crm/contacts?${params}`, auth());
      return data;
    },
    keepPreviousData: true,
  });

  const runImport = useMutation({
    mutationFn: async () => {
      const params = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
      const { data } = await axios.post(`${API}/api/crm/import/run${params}`, null, auth());
      return data;
    },
    onSuccess: () => {
      toast.success('CRM contacts re-synced');
      qc.invalidateQueries({ queryKey: ['crm-contacts'] });
      qc.invalidateQueries({ queryKey: ['crm-dashboard'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Import failed'),
  });

  const create = useMutation({
    mutationFn: async () => {
      if (!tenantId) throw new Error('Please pick a tenant first');
      const body = { tenant_id: tenantId, ...newC };
      const { data } = await axios.post(`${API}/api/crm/contacts`, body, auth());
      return data;
    },
    onSuccess: () => {
      toast.success('Contact added');
      setOpen(false);
      setNewC({ full_name: '', email: '', phone: '', role: '', organisation: '' });
      qc.invalidateQueries({ queryKey: ['crm-contacts'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || 'Create failed'),
  });

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / 50));

  return (
    <div className="space-y-4" data-testid="crm-contacts-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-0 sm:min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            placeholder="Search by name, email, phone…"
            value={q}
            onChange={e => { setQ(e.target.value); setPage(1); }}
            className="pl-9 bg-slate-900/60 border-slate-800"
            data-testid="crm-search-input"
          />
        </div>
        <Select value={role || 'all'} onValueChange={v => { setRole(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-full max-w-[160px] bg-slate-900/60 border-slate-800" data-testid="crm-role-filter">
            <SelectValue placeholder="All roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {['delegate', 'student', 'parent', 'staff', 'alumni', 'member', 'donor'].map(r =>
              <SelectItem key={r} value={r}>{r}</SelectItem>
            )}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => runImport.mutate()}
                disabled={runImport.isPending} data-testid="crm-resync-btn">
          <RefreshCw className={`h-4 w-4 mr-1 ${runImport.isPending ? 'animate-spin' : ''}`} />
          Re-sync
        </Button>
        {['csv', 'xlsx', 'pdf'].map(fmt => (
          <Button key={fmt} variant="outline" size="sm"
                   data-testid={`crm-export-${fmt}`}
                   onClick={async () => {
                     try {
                       const params = new URLSearchParams();
                       if (q) params.set('q', q);
                       if (role) params.set('role', role);
                       const res = await fetch(
                         `${API}/api/crm/export/contacts.${fmt}?${params.toString()}`,
                         { headers: { Authorization: `Bearer ${getToken()}` } }
                       );
                       if (!res.ok) throw new Error(`HTTP ${res.status}`);
                       const blob = await res.blob();
                       const url = window.URL.createObjectURL(blob);
                       const a = document.createElement('a');
                       a.href = url;
                       a.download = `crm-contacts.${fmt}`;
                       document.body.appendChild(a);
                       a.click();
                       a.remove();
                       window.URL.revokeObjectURL(url);
                     } catch (e) { toast.error(`Export failed: ${e.message}`); }
                   }}>
            <Download className="h-3.5 w-3.5 mr-1" /> {fmt.toUpperCase()}
          </Button>
        ))}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="crm-add-contact-btn"><Plus className="h-4 w-4 mr-1" />Add contact</Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800">
            <DialogHeader><DialogTitle>New CRM contact</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Full name *" value={newC.full_name}
                     onChange={e => setNewC({ ...newC, full_name: e.target.value })}
                     data-testid="new-contact-name" />
              <Input placeholder="Email" type="email" value={newC.email}
                     onChange={e => setNewC({ ...newC, email: e.target.value })}
                     data-testid="new-contact-email" />
              <Input placeholder="Phone" value={newC.phone}
                     onChange={e => setNewC({ ...newC, phone: e.target.value })}
                     data-testid="new-contact-phone" />
              <Input placeholder="Organisation" value={newC.organisation}
                     onChange={e => setNewC({ ...newC, organisation: e.target.value })} />
              <Input placeholder="Role (lead, partner, donor…)" value={newC.role}
                     onChange={e => setNewC({ ...newC, role: e.target.value })} />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={() => create.mutate()} disabled={!newC.full_name || create.isPending}
                      data-testid="new-contact-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 border-b border-slate-800">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Name</th>
                <th className="text-left px-4 py-2 font-medium">Email</th>
                <th className="text-left px-4 py-2 font-medium">Phone</th>
                <th className="text-left px-4 py-2 font-medium">Roles</th>
                <th className="text-left px-4 py-2 font-medium">Links</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody data-testid="crm-contacts-table">
              {isLoading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  No contacts yet. Hit <strong>Re-sync</strong> to import from existing modules.
                </td></tr>
              )}
              {rows.map(c => (
                <tr key={c.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition">
                  <td className="px-4 py-2">
                    <Link to={`/admin/crm/contacts/${c.id}`}
                          className="text-slate-100 hover:text-indigo-300"
                          data-testid={`crm-contact-link-${c.id}`}>
                      {c.full_name || '—'}
                    </Link>
                    {c.do_not_contact && (
                      <Badge variant="destructive" className="ml-2 text-[10px]">DNC</Badge>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-400">{c.primary_email || '—'}</td>
                  <td className="px-4 py-2 text-slate-400">{c.primary_phone || '—'}</td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1 flex-wrap">
                      {(c.roles || []).slice(0, 3).map(r => (
                        <Badge key={r} variant="outline" className="text-[10px]">{r}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-slate-400 tabular-nums">
                    {(c.linked_records || []).length}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Link to={`/admin/crm/contacts/${c.id}`}>
                      <ChevronRight className="h-4 w-4 text-slate-500" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>Page {page} of {totalPages} • {total} total</span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" disabled={page === 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</Button>
            <Button size="sm" variant="ghost" disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 3 — Pipeline (Kanban)
// ---------------------------------------------------------------------------
const PipelineTab = ({ tenantId }) => {
  const qc = useQueryClient();
  const [openOpp, setOpenOpp] = useState(false);
  const [newOpp, setNewOpp] = useState({ title: '', value: 0, contact_id: '' });

  const { data: board, isLoading } = useQuery({
    queryKey: ['crm-board', tenantId],
    queryFn: async () => {
      if (!tenantId) return null;
      const { data } = await axios.get(
        `${API}/api/crm/opportunities/board?tenant_id=${encodeURIComponent(tenantId)}`, auth()
      );
      return data;
    },
    enabled: !!tenantId,
  });

  const seedDefault = useMutation({
    mutationFn: async () => {
      const { data } = await axios.post(
        `${API}/api/crm/pipelines/seed-default?tenant_id=${encodeURIComponent(tenantId)}`,
        null, auth()
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Default sales pipeline created');
      qc.invalidateQueries({ queryKey: ['crm-board'] });
    },
  });

  const createOpp = useMutation({
    mutationFn: async () => {
      const body = {
        tenant_id: tenantId,
        pipeline_id: board.pipeline.id,
        title: newOpp.title,
        value: Number(newOpp.value) || 0,
        contact_id: newOpp.contact_id || null,
      };
      const { data } = await axios.post(`${API}/api/crm/opportunities`, body, auth());
      return data;
    },
    onSuccess: () => {
      toast.success('Opportunity created');
      setOpenOpp(false);
      setNewOpp({ title: '', value: 0, contact_id: '' });
      qc.invalidateQueries({ queryKey: ['crm-board'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Create failed'),
  });

  const moveOpp = useMutation({
    mutationFn: async ({ opp_id, stage_id }) => {
      await axios.patch(`${API}/api/crm/opportunities/${opp_id}`, { stage_id }, auth());
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm-board'] });
    },
  });

  if (!tenantId) {
    return (
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-6 text-sm text-slate-400">
          Select a tenant above to manage its pipeline.
        </CardContent>
      </Card>
    );
  }

  if (isLoading) return <p className="text-slate-400 text-sm">Loading pipeline…</p>;

  if (!board?.pipeline) {
    return (
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-6 space-y-3">
          <p className="text-slate-300">No pipeline configured for this tenant yet.</p>
          <Button onClick={() => seedDefault.mutate()} disabled={seedDefault.isPending}
                  data-testid="crm-seed-pipeline-btn">
            <Workflow className="h-4 w-4 mr-1" />Create default Sales Pipeline
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="crm-pipeline-tab">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm text-slate-300 font-medium">{board.pipeline.name}</div>
          <div className="text-xs text-slate-500">{board.pipeline.description}</div>
        </div>
        <Dialog open={openOpp} onOpenChange={setOpenOpp}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="crm-add-opp-btn"><Plus className="h-4 w-4 mr-1" />New opportunity</Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800">
            <DialogHeader><DialogTitle>New opportunity</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Deal title *" value={newOpp.title}
                     onChange={e => setNewOpp({ ...newOpp, title: e.target.value })}
                     data-testid="new-opp-title" />
              <Input placeholder="Value (USD)" type="number" value={newOpp.value}
                     onChange={e => setNewOpp({ ...newOpp, value: e.target.value })}
                     data-testid="new-opp-value" />
              <Input placeholder="Contact ID (optional)" value={newOpp.contact_id}
                     onChange={e => setNewOpp({ ...newOpp, contact_id: e.target.value })} />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpenOpp(false)}>Cancel</Button>
              <Button onClick={() => createOpp.mutate()}
                      disabled={!newOpp.title || createOpp.isPending}
                      data-testid="new-opp-save">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto pb-3">
        <div className="flex gap-3 min-w-max">
          {(board.columns || []).map(col => (
            <div key={col.stage.id} className="w-60 shrink-0">
              <div className="flex items-center justify-between mb-2 px-1">
                <div className="text-xs uppercase tracking-wider text-slate-400">
                  {col.stage.name}
                </div>
                <div className="text-xs text-slate-500 tabular-nums">
                  {col.count} · ${col.total_value.toLocaleString()}
                </div>
              </div>
              <div className="space-y-2"
                   data-testid={`crm-kanban-col-${col.stage.name.toLowerCase()}`}
                   onDragOver={e => e.preventDefault()}
                   onDrop={e => {
                     const opp_id = e.dataTransfer.getData('opp_id');
                     if (opp_id) moveOpp.mutate({ opp_id, stage_id: col.stage.id });
                   }}>
                {col.opportunities.map(o => (
                  <div key={o.id}
                       draggable
                       onDragStart={e => e.dataTransfer.setData('opp_id', o.id)}
                       className="bg-slate-900/80 border border-slate-800 rounded-md p-3 hover:border-indigo-500/60 transition cursor-grab active:cursor-grabbing"
                       data-testid={`crm-opp-card-${o.id}`}>
                    <div className="text-sm text-slate-100 font-medium">{o.title}</div>
                    <div className="text-xs text-slate-400 mt-1">
                      ${Number(o.value || 0).toLocaleString()} {o.currency}
                    </div>
                    {o.contact_id && (
                      <Link to={`/admin/crm/contacts/${o.contact_id}`}
                            className="text-[10px] text-indigo-300 hover:underline mt-1 inline-flex items-center gap-1">
                        contact <ArrowRight className="h-3 w-3" />
                      </Link>
                    )}
                  </div>
                ))}
                {col.opportunities.length === 0 && (
                  <div className="text-[11px] text-slate-600 italic px-2 py-3">empty</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 4 — Tasks
// ---------------------------------------------------------------------------
const TasksTab = ({ tenantId }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['crm-tasks-all', tenantId],
    queryFn: async () => {
      const params = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
      const { data } = await axios.get(`${API}/api/crm/tasks${params}`, auth());
      return data;
    },
  });
  const complete = useMutation({
    mutationFn: async (id) => {
      await axios.post(`${API}/api/crm/tasks/${id}/complete`, null, auth());
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm-tasks-all'] }),
  });
  if (isLoading) return <p className="text-slate-400 text-sm">Loading…</p>;
  const tasks = data || [];
  return (
    <div className="space-y-2" data-testid="crm-tasks-tab">
      {tasks.length === 0 && (
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-6 text-sm text-slate-400">
            No CRM tasks yet. Tasks created against opportunities or contacts will show up here.
          </CardContent>
        </Card>
      )}
      {tasks.map(t => (
        <Card key={t.id} className="bg-slate-900/60 border-slate-800" data-testid={`crm-task-${t.id}`}>
          <CardContent className="p-3 flex items-center gap-3">
            <ListChecks className={`h-4 w-4 ${t.status === 'done' ? 'text-emerald-400' : 'text-slate-400'}`} />
            <div className="flex-1">
              <div className={`text-sm ${t.status === 'done' ? 'line-through text-slate-500' : 'text-slate-200'}`}>
                {t.title}
              </div>
              {t.description && <div className="text-xs text-slate-500">{t.description}</div>}
              <div className="text-[10px] text-slate-500 mt-1 flex gap-2">
                {t.priority && <span>priority: {t.priority}</span>}
                {t.due_date && <span>due: {t.due_date}</span>}
              </div>
            </div>
            {t.status !== 'done' && (
              <Button size="sm" variant="ghost" onClick={() => complete.mutate(t.id)}
                      data-testid={`crm-task-done-${t.id}`}>
                Mark done
              </Button>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const AdminCRM = () => {
  const { user } = useAuthStore();
  const [tenantId, setTenantId] = useState(user?.tenant_id || '');
  const [tab, setTab] = useState('overview');

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-white">Relationship Management</h1>
            <p className="text-sm text-slate-400 mt-1">
              Unified people graph across delegates, students, donors, alumni & staff.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Tenant ID (leave blank = all)"
              value={tenantId}
              onChange={e => setTenantId(e.target.value)}
              className="w-full max-w-[240px] bg-slate-900/60 border-slate-800"
              data-testid="crm-tenant-input"
            />
          </div>
        </header>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-900/60 border border-slate-800">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
            <TabsTrigger value="pipeline" data-testid="tab-pipeline">Pipeline</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-5">
            <OverviewTab tenantId={tenantId} />
          </TabsContent>
          <TabsContent value="contacts" className="mt-5">
            <ContactsTab tenantId={tenantId} />
          </TabsContent>
          <TabsContent value="pipeline" className="mt-5">
            <PipelineTab tenantId={tenantId} />
          </TabsContent>
          <TabsContent value="tasks" className="mt-5">
            <TasksTab tenantId={tenantId} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminCRM;
